import React, { useEffect, useState } from 'react';
import { RemoteParticipant } from 'twilio-video';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import Snackbar from '@material-ui/core/Snackbar';
import { SnackbarContent } from '@material-ui/core';

import { HOST_IDENTITY } from '../../constants';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { participantLabel } from '../../utils/participantLabels';

const useStyles = makeStyles({
  snackbar: {
    backgroundColor: '#6db1ff',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: '0.8em',
  },
});

/**
 * Tells the host that the guest left and may rejoin with the same link. The host stays in the call
 * (see useAutoDisconnectOnEmpty), so without this they would see only an empty room. Shown on the host's
 * side only, and cleared when the guest comes back.
 */
export default function GuestLeftNotification() {
  const classes = useStyles();
  const { room } = useVideoContext();
  const [whoLeft, setWhoLeft] = useState<string | null>(null);

  useEffect(() => {
    if (room.localParticipant?.identity !== HOST_IDENTITY) {
      return;
    }
    const left = (participant: RemoteParticipant) => {
      if (room.participants.size === 0) setWhoLeft(participant.identity);
    };
    const joined = () => setWhoLeft(null);
    room.on('participantDisconnected', left);
    room.on('participantConnected', joined);
    return () => {
      room.off('participantDisconnected', left);
      room.off('participantConnected', joined);
    };
  }, [room, room.localParticipant]);

  return (
    <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} open={whoLeft !== null}>
      <SnackbarContent
        className={classes.snackbar}
        message={
          <span className={classes.message}>
            <InfoIcon className={classes.icon} />
            {whoLeft ? participantLabel(whoLeft) : ''} left the call. They can rejoin with the same link.
          </span>
        }
      />
    </Snackbar>
  );
}
